/**
 * Phase F3 — Provider Compliance Center
 * --------------------------------------
 * Surfaces Phase 2 compliance APIs to the provider (broker) UI.
 *
 * Endpoints used:
 *   GET    /api/compliance/me                       — profile + status_history + credentials
 *   POST   /api/compliance/submit/provider          — submit / re-submit profile + provider_types
 *   POST   /api/compliance/me/credentials           — add one credential row
 *   DELETE /api/compliance/me/credentials/{idx}     — remove credential row
 *
 * Backend invariants surfaced:
 *   status            ∈ COMPLIANCE_REQUIRED | COMPLIANCE_PENDING | COMPLIANCE_APPROVED | COMPLIANCE_REJECTED
 *   renewal_status    ∈ NOT_DUE | DUE_SOON | OVERDUE
 *   verification      ∈ UNVERIFIED | VERIFIED | REJECTED
 *
 * Pure UI. No backend logic modified.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, ShieldAlert, Shield, Clock, FileText, History, AlertCircle,
  Save, Plus, Trash2, Award,
} from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDER_TYPES = [
  'BROKER', 'FINANCIAL_ADVISOR', 'WEALTH_MANAGER', 'DAY_TRADER',
  'INVESTMENT_MANAGER', 'UTILITY_PROVIDER', 'SERVICE_PROVIDER', 'OTHER',
];

const STATUS_META = {
  COMPLIANCE_REQUIRED: { label: 'Compliance Required', color: theme.colors.status.warning, Icon: ShieldAlert,
    description: 'Action needed — complete your provider compliance profile.' },
  COMPLIANCE_PENDING:  { label: 'Pending Review', color: theme.colors.status.info, Icon: Clock,
    description: 'Submitted. An admin is reviewing your profile.' },
  COMPLIANCE_APPROVED: { label: 'Compliance Approved', color: theme.colors.status.success, Icon: ShieldCheck,
    description: 'You have full provider access.' },
  COMPLIANCE_REJECTED: { label: 'Compliance Rejected', color: theme.colors.status.error, Icon: ShieldAlert,
    description: 'Your profile was rejected. Review notes and resubmit.' },
};

const RENEWAL_META = {
  NOT_DUE:  { label: 'Renewal Not Due', color: theme.colors.text.muted },
  DUE_SOON: { label: 'Renewal Due Soon', color: theme.colors.status.warning },
  OVERDUE:  { label: 'Renewal Overdue', color: theme.colors.status.error },
};

const VERIFY_META = {
  VERIFIED:   { label: 'Verified', color: theme.colors.status.success },
  UNVERIFIED: { label: 'Unverified', color: theme.colors.text.muted },
  REJECTED:   { label: 'Rejected', color: theme.colors.status.error },
};

const inputStyle = {
  width: '100%', padding: '10px 12px', background: theme.colors.bg.page,
  border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.sm,
  color: theme.colors.text.primary, fontSize: 13, outline: 'none', boxSizing: 'border-box',
};
const labelStyle = { display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: theme.colors.text.secondary };
const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, padding: 22, boxShadow: theme.colors.shadows.sm,
};
const sectionTitle = {
  fontSize: 15, fontWeight: 700, color: theme.colors.text.primary,
  marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8,
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || { label: status || 'Unknown', color: theme.colors.text.muted, Icon: Shield };
  const Icon = meta.Icon;
  return (
    <span data-testid={`provider-compliance-status-${status}`} style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '6px 14px', borderRadius: 999,
      background: `${meta.color}1A`, color: meta.color,
      fontSize: 13, fontWeight: 700,
    }}>
      <Icon size={14} /> {meta.label}
    </span>
  );
};

const RenewalPill = ({ status }) => {
  const meta = RENEWAL_META[status] || { label: status || '—', color: theme.colors.text.muted };
  return (
    <span style={{
      padding: '5px 12px', borderRadius: 999,
      background: `${meta.color}1A`, color: meta.color,
      fontSize: 12, fontWeight: 700,
    }}>{meta.label}</span>
  );
};

const VerifyPill = ({ status }) => {
  const meta = VERIFY_META[status] || { label: status || '—', color: theme.colors.text.muted };
  return (
    <span style={{
      padding: '3px 8px', borderRadius: 999,
      background: `${meta.color}1A`, color: meta.color,
      fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
    }}>{meta.label}</span>
  );
};

export default function ProviderComplianceCenter() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    legal_name: '', country: 'US', state_region: '', tax_id_last4: '',
    provider_types: ['BROKER'], biography: '', profile_image_url: '',
    company_name: '', website: '', service_description: '', notes: '',
  });
  const [newCred, setNewCred] = useState({
    license_number: '', license_type: '', issuing_authority: '', expiration_date: '',
  });

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/compliance/me`, auth);
      const p = res.data?.profile || null;
      setProfile(p);
      if (p) {
        setForm((f) => ({
          ...f,
          legal_name: p.legal_name || '',
          country: p.country || 'US',
          state_region: p.state_region || '',
          tax_id_last4: p.tax_id_last4 || '',
          provider_types: p.provider_types?.length ? p.provider_types : ['BROKER'],
          biography: p.biography || '',
          profile_image_url: p.profile_image_url || '',
          company_name: p.company_name || '',
          website: p.website || '',
          service_description: p.service_description || '',
          notes: p.notes || '',
        }));
      }
    } catch (e) {
      toast.error('Failed to load compliance profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggleProviderType = (t) => {
    setForm((f) => {
      const has = f.provider_types.includes(t);
      const next = has ? f.provider_types.filter((x) => x !== t) : [...f.provider_types, t];
      return { ...f, provider_types: next.length ? next : ['BROKER'] };
    });
  };

  const submit = async () => {
    if (!form.legal_name || !form.country || !form.provider_types.length) {
      toast.error('Legal name, country, and at least one provider type are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Strip empty strings to undefined for optional fields
      Object.keys(payload).forEach((k) => { if (payload[k] === '') delete payload[k]; });
      const res = await axios.post(`${API}/compliance/submit/provider`, payload, auth);
      setProfile(res.data?.profile || null);
      toast.success('Compliance profile submitted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed');
    } finally {
      setSaving(false);
    }
  };

  const addCredential = async () => {
    if (!newCred.license_number || !newCred.license_type || !newCred.issuing_authority) {
      toast.error('License number, type, and issuing authority are required.');
      return;
    }
    try {
      const payload = { ...newCred };
      if (!payload.expiration_date) delete payload.expiration_date;
      const res = await axios.post(`${API}/compliance/me/credentials`, payload, auth);
      setProfile(res.data?.profile || profile);
      setNewCred({ license_number: '', license_type: '', issuing_authority: '', expiration_date: '' });
      toast.success('Credential added');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add credential failed');
    }
  };

  const deleteCredential = async (idx) => {
    try {
      const res = await axios.delete(`${API}/compliance/me/credentials/${idx}`, auth);
      setProfile(res.data?.profile || profile);
      toast.success('Credential removed');
    } catch (e) {
      toast.error('Remove failed');
    }
  };

  if (loading) {
    return <div style={{ padding: 24, color: theme.colors.text.muted }}>Loading…</div>;
  }

  const status = profile?.status || 'COMPLIANCE_REQUIRED';
  const renewal = profile?.renewal_status || 'NOT_DUE';
  const meta = STATUS_META[status];
  const credentials = profile?.credentials || [];
  const history = profile?.approval_history || [];

  return (
    <div data-testid="provider-compliance-center-page" style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 800, color: theme.colors.text.primary, margin: 0,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <ShieldCheck size={26} color={theme.colors.primary} />
          Provider Compliance Center
        </h1>
        <p style={{ color: theme.colors.text.secondary, marginTop: 4 }}>
          Submit your compliance profile, manage credentials, and track renewal reminders.
        </p>
      </div>

      {/* Status banner */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
          <StatusPill status={status} />
          <RenewalPill status={renewal} />
        </div>
        <div style={{ color: theme.colors.text.secondary, fontSize: 14 }}>
          {meta?.description || ''}
        </div>
        <div style={{
          marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12,
        }}>
          {[
            ['Submitted', fmtDate(profile?.submitted_at)],
            ['Approved', fmtDate(profile?.approved_at)],
            ['Expiration', fmtDate(profile?.expiration_date)],
            ['Renewal Due', fmtDate(profile?.renewal_due_date)],
          ].map(([k, v]) => (
            <div key={k} style={{
              padding: 12, background: theme.colors.bg.secondary, borderRadius: theme.radius.sm,
            }}>
              <div style={{ fontSize: 11, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{k}</div>
              <div style={{ fontWeight: 700, color: theme.colors.text.primary, marginTop: 2 }}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Profile form */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={sectionTitle}><FileText size={16} color={theme.colors.primary} /> Compliance Profile</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Legal Name *</label>
            <input data-testid="provider-legal-name" style={inputStyle} value={form.legal_name}
              onChange={(e) => setForm({ ...form, legal_name: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>Country (ISO-2) *</label>
            <input data-testid="provider-country" style={inputStyle} value={form.country}
              onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })} maxLength={3} />
          </div>
          <div>
            <label style={labelStyle}>Region / State</label>
            <input style={inputStyle} value={form.state_region}
              onChange={(e) => setForm({ ...form, state_region: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>Tax ID (last 4)</label>
            <input style={inputStyle} value={form.tax_id_last4} maxLength={4}
              onChange={(e) => setForm({ ...form, tax_id_last4: e.target.value })} />
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Provider Types *</label>
          <div data-testid="provider-types-grid" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {PROVIDER_TYPES.map((t) => {
              const active = form.provider_types.includes(t);
              return (
                <button
                  key={t}
                  data-testid={`provider-type-${t}`}
                  onClick={() => toggleProviderType(t)}
                  style={{
                    padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
                    border: `1px solid ${active ? theme.colors.primary : theme.colors.borders}`,
                    background: active ? `${theme.colors.primary}15` : theme.colors.bg.card,
                    color: active ? theme.colors.primary : theme.colors.text.secondary,
                    fontSize: 12, fontWeight: 600,
                  }}
                >
                  {t.replace(/_/g, ' ')}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Company Name</label>
            <input style={inputStyle} value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>Website</label>
            <input style={inputStyle} value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>Profile Image URL</label>
            <input style={inputStyle} value={form.profile_image_url}
              onChange={(e) => setForm({ ...form, profile_image_url: e.target.value })} />
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Biography</label>
          <textarea style={{ ...inputStyle, minHeight: 70, fontFamily: 'inherit' }}
            value={form.biography} onChange={(e) => setForm({ ...form, biography: e.target.value })} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Service Description</label>
          <textarea style={{ ...inputStyle, minHeight: 70, fontFamily: 'inherit' }}
            value={form.service_description} onChange={(e) => setForm({ ...form, service_description: e.target.value })} />
        </div>
        <button
          data-testid="provider-submit-btn"
          disabled={saving}
          onClick={submit}
          style={{
            padding: '10px 18px', borderRadius: theme.radius.cta, border: 'none',
            background: theme.colors.primary, color: '#fff', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 13,
          }}
        >
          <Save size={14} /> {saving ? 'Submitting…' : 'Submit / Resubmit Profile'}
        </button>
      </div>

      {/* Credentials */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={sectionTitle}><Award size={16} color={theme.colors.primary} /> Credentials & License Verification</div>
        {credentials.length === 0 ? (
          <div style={{ color: theme.colors.text.muted, fontSize: 13, marginBottom: 12 }}>
            No credentials on file yet.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8, marginBottom: 14 }}>
            {credentials.map((c, i) => (
              <div
                key={`${c.license_number}-${i}`}
                data-testid={`provider-credential-${i}`}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 14px', background: theme.colors.bg.secondary,
                  borderRadius: theme.radius.sm, gap: 12, flexWrap: 'wrap',
                }}
              >
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div style={{ fontWeight: 700, color: theme.colors.text.primary }}>
                    {c.license_type} · {c.license_number}
                  </div>
                  <div style={{ fontSize: 12, color: theme.colors.text.secondary, marginTop: 2 }}>
                    {c.issuing_authority} · Expires {c.expiration_date || '—'}
                  </div>
                </div>
                <VerifyPill status={c.verification_status} />
                <button
                  data-testid={`provider-credential-delete-${i}`}
                  onClick={() => deleteCredential(i)}
                  style={{
                    padding: '6px 10px', borderRadius: theme.radius.sm, cursor: 'pointer',
                    border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.card,
                    display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12,
                    color: theme.colors.status.error, fontWeight: 600,
                  }}
                >
                  <Trash2 size={12} /> Remove
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, alignItems: 'end' }}>
          <div>
            <label style={labelStyle}>License Number</label>
            <input data-testid="new-cred-license-number" style={inputStyle} value={newCred.license_number}
              onChange={(e) => setNewCred({ ...newCred, license_number: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>License Type</label>
            <input data-testid="new-cred-license-type" style={inputStyle} value={newCred.license_type}
              onChange={(e) => setNewCred({ ...newCred, license_type: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>Issuing Authority</label>
            <input data-testid="new-cred-issuing-authority" style={inputStyle} value={newCred.issuing_authority}
              onChange={(e) => setNewCred({ ...newCred, issuing_authority: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>Expiration Date (YYYY-MM-DD)</label>
            <input style={inputStyle} value={newCred.expiration_date}
              onChange={(e) => setNewCred({ ...newCred, expiration_date: e.target.value })} />
          </div>
          <button
            data-testid="add-credential-btn"
            onClick={addCredential}
            style={{
              padding: '10px 14px', borderRadius: theme.radius.cta, border: 'none',
              background: theme.colors.primary, color: '#fff', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 13,
              height: 38,
            }}
          >
            <Plus size={14} /> Add Credential
          </button>
        </div>
      </div>

      {/* Approval history */}
      <div style={cardStyle}>
        <div style={sectionTitle}><History size={16} color={theme.colors.primary} /> Approval History</div>
        {history.length === 0 ? (
          <div style={{ color: theme.colors.text.muted, fontSize: 13 }}>No history yet.</div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {history.map((h, i) => (
              <div key={i} data-testid={`provider-history-${i}`} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                background: theme.colors.bg.secondary, borderRadius: theme.radius.sm,
              }}>
                <AlertCircle size={14} color={theme.colors.text.secondary} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: theme.colors.text.primary, fontSize: 13 }}>
                    {h.action} · {h.actor_role}
                  </div>
                  <div style={{ fontSize: 12, color: theme.colors.text.secondary, marginTop: 2 }}>
                    {h.actor_email || h.actor_id} · {fmtDate(h.date)}
                  </div>
                  {h.notes && <div style={{ fontSize: 12, color: theme.colors.text.muted, marginTop: 4 }}>{h.notes}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
